import { useEffect, useState } from "react";
import styled from "styled-components";
import { Formik, FormikHelpers } from "formik";
import moment from "moment";

import { AccountComponent } from "@components";
import { breakpoint } from "@utilities";
import { accountSchema } from "@validations";
import { IAccountInfo, IAccountResponse, ICountryData } from "@interfaces";
import { selectAccount, updateAccountDetail, useAppDispatch, useAppSelector } from "@redux";
import { externalApi } from "@api";

/**
 * "My Details" tab — the account form previously hosted on the standalone
 * Update-My-Details page. On success it surfaces an inline confirmation instead
 * of navigating away, so the user stays inside the Profile tabs.
 */
export const ProfileDetailsTab = () => {
    const { account } = useAppSelector(selectAccount);

    const accountInfo: IAccountInfo = {
        title: account?.title ?? "",
        first_name: account?.first_name ?? "",
        last_name: account?.last_name ?? "",
        date_of_birth: account?.date_of_birth ?? "",
        email: account?.email ?? "",
        phone_number: account?.phone_number ?? "",
        post_code: account?.post_code ?? "",
        country_code: account?.country_code ?? "",
    };

    const [countriesData, setCountriesData] = useState<ICountryData[]>([]);
    const [savedMessage, setSavedMessage] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        (async () => {
            const res = await externalApi.getCountriesDate();
            setCountriesData(res.data);
        })();
    }, []);

    const handleSubmit = async (
        value: IAccountInfo,
        { setSubmitting }: FormikHelpers<IAccountInfo>,
    ) => {
        try {
            const formattedValue = {
                ...value,
                date_of_birth: value.date_of_birth
                    ? moment(value.date_of_birth).format("DD-MMM-YYYY")
                    : "",
            };

            const res = (await dispatch(updateAccountDetail(formattedValue))) as {
                payload: IAccountResponse;
            };
            if (res && res.payload.code === 200) {
                setSavedMessage("Your details have been saved.");
            }
        } catch (error: any) {
            const message = error?.response?.data?.message || "Something went wrong!";
            console.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormContainer>
            {savedMessage && <SuccessBanner role="status">{savedMessage}</SuccessBanner>}
            <Formik
                initialValues={accountInfo}
                validationSchema={accountSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <AccountComponent
                        values={values}
                        countriesData={countriesData}
                        handleChange={(e: any) => {
                            setSavedMessage(null);
                            handleChange(e);
                        }}
                        handleBlur={handleBlur}
                        handleSubmit={handleSubmit}
                        isUpdateAccount={true}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Formik>
        </FormContainer>
    );
};

const FormContainer = styled.div`
    background-color: #fff;
    padding: 30px 30px 50px 30px;
    border-radius: 4px;

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
    `}
`;

const SuccessBanner = styled.div`
    background-color: #e7f6ec;
    color: #1e7d3a;
    border: 1px solid #b6e2c4;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 20px;
    font-size: 14px;
    font-weight: 500;
`;
